import _ from 'lodash';
import createModel, { prop } from './lib/create_model';

import { NotificationSoundType } from 'actions/notification/lib/notification_constants';
import SidePanel, { SidePanelType } from './side_panel';

const AgentPreferences = createModel({
  modelName: 'AgentPreferences',

  properties: {
    agentNotificationsLastOpenedAt: prop(String),
    answerPanelLastOpenedAt: prop(String),
    answerSearchAudiences: prop([String]),
    answerSearchLanguage: prop(String),
    isTaskCommentFormattingOpen: prop(Boolean),
    shouldCollapseSidebar: prop(Boolean),

    readFeatureNotifications: prop(Object).default({}),
    agentNotificationSound: prop(String).default(NotificationSoundType.AGENT_NOTIFICATION),
    channelNotificationSounds: prop(Object).default({}),
    customizeChannelNotificationSounds: prop(Boolean).default(false),
    channelColors: prop(Object).default({}),
    customizeChannelColors: prop(Boolean).default(false),

    // Use upgraded Slate editors
    experimentalEditorEnabled: prop(Boolean).default(false),

    // Display conversation summarization controls
    conversationSummaryControlsEnabled: prop(Boolean).default(false),

    favoriteAnswerIds: prop([String]).default([]),

    seenFeatureTips: prop(Object).default({}),

    sidePanel: prop(SidePanel).default(
      () =>
        new SidePanel({
          panelShown: SidePanelType.NONE,
          panelLastShownAt: '',
        })
    ),
  },

  setAnswerPanelLastOpenedAt(lastOpenedAt) {
    this.answerPanelLastOpenedAt = lastOpenedAt;
  },

  setSidePanel(sidePanel) {
    this.sidePanel = sidePanel instanceof SidePanel ? sidePanel : new SidePanel(sidePanel);
  },

  setAnswerSearchLanguage(language) {
    this.answerSearchLanguage = language;
  },

  setAnswerSearchAudiences(audiences) {
    this.answerSearchAudiences = audiences;
  },

  setAgentNotificationsLastOpenedAt(lastOpenedAt) {
    this.agentNotificationsLastOpenedAt = lastOpenedAt;
  },

  toggleShouldCollapseSidebar() {
    this.shouldCollapseSidebar = !this.shouldCollapseSidebar;
  },

  toggleIsTaskCommentFormattingOpen() {
    this.isTaskCommentFormattingOpen = !this.isTaskCommentFormattingOpen;
  },

  hasSeenFeatureNotification(feature) {
    return !!this.readFeatureNotifications[feature];
  },

  markFeatureAsSeen(feature) {
    this.readFeatureNotifications[feature] = true;
  },

  setAgentNotificationSound(sound) {
    this.agentNotificationSound = sound;
  },

  setChannelNotificationSound(channel, sound) {
    this.channelNotificationSounds = { ...this.channelNotificationSounds, [channel]: sound };
  },

  setCustomizeChannelNotificationSounds(enabled) {
    this.customizeChannelNotificationSounds = enabled;
  },

  setChannelColor(channel, color) {
    this.channelColors = { ...this.channelColors, [channel]: color };
  },

  setCustomizeChannelColors(enabled) {
    this.customizeChannelColors = enabled;
  },

  toggleExperimentalEditor() {
    this.experimentalEditorEnabled = !this.experimentalEditorEnabled;
  },

  toggleConversationSummaryControls() {
    this.conversationSummaryControlsEnabled = !this.conversationSummaryControlsEnabled;
  },

  isAnswerFavorited(id) {
    return !!_.find(this.favoriteAnswerIds, favId => favId === id);
  },

  toggleFavoriteAnswer(id) {
    if (this.isAnswerFavorited(id)) {
      this.favoriteAnswerIds = _.filter(this.favoriteAnswerIds, favId => favId !== id);
    } else {
      this.favoriteAnswerIds.push(id);
    }
  },

  clearFavoriteAnswerIds() {
    this.favoriteAnswerIds = [];
  },

  hasSeenFeatureTip(feature) {
    return !!this.seenFeatureTips[feature];
  },

  markFeatureTipAsSeen(feature) {
    this.seenFeatureTips[feature] = true;
  },
});

export default AgentPreferences;
